<template>
  <!--
    ผลประเมินที่เลขบัตรไม่อยู่ในกรอบอัตรากำลัง (government_employee เป็นหลัก) — govem_assessment_frame.php (Admin)
    แสดงสถานะ (พ้นสภาพ / มีเฉพาะในทะเบียน / ไม่พบ) และให้ผูกกับคนในกรอบของสถานศึกษาเดียวกันที่ยังไม่มีผลรอบนั้น
  -->
  <div>
    <v-alert v-if="orphans.length" type="warning" dense outlined class="mb-3">
      <div class="d-flex align-center flex-wrap" style="gap:8px">
        <span>
          <strong>ผลประเมินปี {{ year }} ที่เลขบัตรไม่อยู่ในกรอบอัตรากำลัง {{ orphans.length }} รายการ</strong>
          (แสดงเป็น "ไม่อยู่ในกรอบอัตรากำลัง") — ตรวจกับกรอบอัตรากำลังเป็นหลัก
        </span>
        <v-spacer />
        <v-btn small outlined color="warning darken-2" @click="dialog = true">
          <v-icon small left>mdi-account-search</v-icon>ตรวจสอบและแก้ไข
        </v-btn>
      </div>
    </v-alert>

    <v-dialog v-model="dialog" max-width="1150" scrollable>
      <v-card>
        <v-toolbar dense dark flat color="orange darken-3" class="flex-grow-0">
          <v-icon left>mdi-account-alert</v-icon>
          <v-toolbar-title class="subtitle-1">ผลประเมินที่ไม่อยู่ในกรอบอัตรากำลัง — ปี {{ year }}</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-3">
          <v-alert type="info" dense text class="caption">
            สาเหตุที่พบบ่อย: บันทึกผลประเมินด้วยเลขบัตรผิดคน หรือบุคคลไม่อยู่ในกรอบอัตรากำลังของสถานศึกษา ·
            <strong>ผูกกับคนในกรอบ</strong> = แก้เลขบัตรของผลประเมินเป็นคนในกรอบของสถานศึกษาเดียวกันที่ยังไม่มีผลรอบนั้น
            (บันทึกใน Log ขอกู้คืนได้) · กรณีพ้นสภาพ/ไม่อยู่ในกรอบจริง ผลประเมินนี้จะไม่ถูกนำไปคำนวณเลื่อนค่าตอบแทน
          </v-alert>
          <v-data-table :headers="headers" :items="orphans" :loading="loading" dense :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [25, 50, -1] }" no-data-text="ไม่มีรายการ">
            <template v-slot:[`item.id_card`]="{ item }">
              <div class="font-weight-medium">{{ item.registry_name || item.resigned_name || "(ไม่พบชื่อ)" }}</div>
              <div class="caption">{{ item.id_card }}</div>
              <v-chip x-small dark :color="statusColor(item.frame_status)">{{ statusText(item.frame_status) }}</v-chip>
            </template>
            <template v-slot:[`item.college_name`]="{ item }">
              {{ item.college_name }}<div class="caption grey--text">{{ item.college_code }} · รอบ {{ item.assessment_round }}</div>
            </template>
            <template v-slot:[`item.total_score`]="{ item }">
              {{ +item.total_score }}<div class="caption grey--text">{{ item.assessment_status }} · ผู้ประเมิน {{ item.assessor_name || "-" }}</div>
            </template>
            <template v-slot:[`item.fix`]="{ item }">
              <div v-if="item.candidates.length" class="d-flex align-center" style="gap:6px">
                <v-select v-model="pick[item.id]" :items="item.candidates" :item-text="c => `${c.full_name} (ตำแหน่ง ${c.id_position})`"
                  item-value="id_card" dense outlined hide-details placeholder="เลือกคนในกรอบ" style="min-width:280px" />
                <v-btn x-small color="primary" :disabled="!pick[item.id]" :loading="busy === item.id" @click="reassign(item)">ผูกกับคนในกรอบ</v-btn>
              </div>
              <span v-else class="caption grey--text">ไม่มีคนในกรอบของสถานศึกษานี้ที่ยังไม่มีผลรอบ {{ item.assessment_round }}</span>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="5000">{{ snack.text }}</v-snackbar>
  </div>
</template>

<script>
export default {
  name: "AssessmentFrameCheck",
  props: { year: { type: [Number, String], required: true } },
  data() {
    return {
      ApiKey: "HRvec2021",
      orphans: [],
      loading: false,
      dialog: false,
      busy: null,
      pick: {},
      snack: { show: false, text: "", color: "success" },
      headers: [
        { text: "บุคคลในผลประเมิน", value: "id_card", width: 230 },
        { text: "สถานศึกษาผู้ประเมิน", value: "college_name" },
        { text: "คะแนน", value: "total_score", width: 170 },
        { text: "แก้ไข", value: "fix", sortable: false }
      ]
    };
  },
  watch: {
    year() {
      this.load();
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    statusText(s) {
      return { resigned: "พ้นสภาพแล้ว", registry_only: "มีในทะเบียน ไม่อยู่ในกรอบ", not_found: "ไม่อยู่ในกรอบอัตรากำลัง" }[s] || s;
    },
    statusColor(s) {
      return { resigned: "grey darken-1", registry_only: "orange darken-2", not_found: "red darken-1" }[s] || "grey";
    },
    async api(payload) {
      try {
        const res = await this.$http.post("govem_assessment_frame.php", { ApiKey: this.ApiKey, ...payload });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    async load() {
      if (!this.year) return;
      this.loading = true;
      try {
        this.orphans = (await this.api({ action: "orphans", assessment_year: +this.year })).orphans;
      } catch (e) {
        this.orphans = [];
      } finally {
        this.loading = false;
      }
    },
    async reassign(item) {
      const c = item.candidates.find(x => x.id_card === this.pick[item.id]);
      if (!confirm(`ผูกผลประเมินรอบ ${item.assessment_round} (คะแนน ${+item.total_score}) จากเลขบัตร ${item.id_card}\nไปเป็นของ ${c.full_name} (${c.id_card})?`)) return;
      this.busy = item.id;
      try {
        await this.api({ action: "reassign", assessment_id: item.id, id_card: c.id_card });
        this.snack = { show: true, text: `ผูกผลประเมินกับ ${c.full_name} แล้ว`, color: "success" };
        await this.load();
        this.$emit("changed");
      } catch (e) {
        this.snack = { show: true, text: e.message, color: "error" };
      } finally {
        this.busy = null;
      }
    }
  }
};
</script>
